import {
  closeOverlay,
  openOverlay,
} from "./OverlayManager";

// NapflixOS Notification Manager v7
// Lightweight OS notification runtime built on the overlay stack foundation.
const DEFAULT_DURATIONS = {
  info: 4000,
  success: 4000,
  warning: 6000,
  error: 8000,
};

const SUPPORTED_LEVELS = new Set(["info", "success", "warning", "error"]);

let notifications = [];
const listeners = new Set();
const timers = new Map();

const notifyListeners = () => {
  listeners.forEach((listener) => listener(notifications));
};

const normalizeNotification = (notification) => {
  const level = SUPPORTED_LEVELS.has(notification.level)
    ? notification.level
    : "info";

  return {
    id:
      notification.id ||
      `notification-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    type: notification.type || "system",
    title: notification.title || "",
    message: notification.message || "",
    level,
    duration: notification.duration ?? DEFAULT_DURATIONS[level],
    createdAt: notification.createdAt || Date.now(),
  };
};

const syncNotificationOverlay = () => {
  if (notifications.length === 0) {
    closeOverlay("napflix-notifications");
    return;
  }

  openOverlay({
    id: "napflix-notifications",
    type: "notifications",
    priority: 30,
    modal: false,
    focusTrap: false,
    payload: { count: notifications.length },
  });
};

export function getNotifications() {
  return notifications;
}

export function pushNotification(notification) {
  const nextNotification = normalizeNotification(notification);

  notifications = [
    ...notifications.filter((item) => item.id !== nextNotification.id),
    nextNotification,
  ];

  window.clearTimeout(timers.get(nextNotification.id));
  timers.delete(nextNotification.id);

  if (nextNotification.duration > 0) {
    timers.set(
      nextNotification.id,
      window.setTimeout(() => {
        removeNotification(nextNotification.id);
      }, nextNotification.duration)
    );
  }

  syncNotificationOverlay();
  notifyListeners();
  return nextNotification;
}

export function removeNotification(id) {
  if (!id) return;

  window.clearTimeout(timers.get(id));
  timers.delete(id);
  notifications = notifications.filter((notification) => notification.id !== id);

  syncNotificationOverlay();
  notifyListeners();
}

export function clearNotifications() {
  timers.forEach((timer) => window.clearTimeout(timer));
  timers.clear();
  notifications = [];
  closeOverlay("napflix-notifications");
  notifyListeners();
}

export function subscribeNotifications(listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
